import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Banknote } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
import type { PublicService } from '../types';
import { formatPrice } from '../types';
import { BookingStepper } from '../components/BookingStepper';

export function ServiceSelectionPage() {
  const navigate = useNavigate();
  const { services, setSelectedService, businessListing } = useBooking();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const handleServiceSelect = (service: PublicService) => {
    setSelectedService(service);
    navigate('/book/date-time');
  };

  const categories = useMemo(() => {
    const cats = new Set(services.map((s) => s.category).filter(Boolean) as string[]);
    return Array.from(cats);
  }, [services]);

  const showChips = categories.length > 1;

  const filteredServices = useMemo(() => {
    if (selectedCategory === 'All') return services;
    return services.filter((s) => s.category === selectedCategory);
  }, [services, selectedCategory]);

  if (!businessListing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-site-muted" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-site-bg text-site-text">
      {/* Dynamic accent hover styles */}
      <style>{`
        .service-card-accent:hover { border-color: var(--site-primary) !important; }
        .service-card-accent:hover .service-title { color: var(--site-link) !important; }
      `}</style>

      {/* Header with Stepper */}
      <BookingStepper currentStep={1} />

      {/* Services */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-site-text mb-2">
            Choose a Service
          </h2>
          <p className="text-site-muted">
            Select the service you'd like to book
          </p>

          {showChips && (
            <div className="flex flex-wrap gap-2 mt-4">
              {['All', ...categories].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-site-primary text-site-on-primary border border-site-primary-outline shadow-sm'
                      : 'bg-site-bg text-site-muted border border-site-border hover:border-site-muted'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-4">
          {filteredServices.map((service) => (
            <button
              key={service.id}
              onClick={() => handleServiceSelect(service)}
              className="group service-card-accent bg-site-bg rounded-2xl border-2 border-site-border p-6 text-left transition-all hover:shadow-lg"
            >
              <div className="flex gap-4">
                {service.image_url && (
                  <img
                    src={service.image_url}
                    alt={service.name}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                )}

                <div className="flex-1">
                  <h4 className="service-title text-xl font-semibold text-site-text mb-1 transition-colors">
                    {service.name}
                  </h4>

                  {service.description && (
                    <p className="text-site-muted mb-3 line-clamp-2">
                      {service.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-site-muted">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{service.duration_minutes} min</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Banknote className="w-4 h-4" />
                      <span>{formatPrice(service.price_cents, service.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {services.length === 0 && (
          <div className="text-center py-12">
            <p className="text-site-muted">No services available at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
}
